<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import LocationIcon from '../assets/icon/location.svg';
import DeleteIcon from '../assets/icon/trash.svg';
import debounce from '@/utils/debounce';
import { COUNTRY_CODE } from '@/utils/constants';
import GoogleAutocomplete from './GoogleAutocomplete.vue';
import IdealPostcodesAutocomplete from './IdealPostcodesAutocomplete.vue';
import WarningBox from './WarningBox.vue';
import { SiteFormModel } from '../models/Project';
import { useProjectStore } from '../stores/projectStore';
import type { AddressSelectedPayload } from '../types/ideal-postcodes';
import { isIdealPostcodesFeatureEnabled } from '../utils/ideal-postcodes-config';

const emit = defineEmits(['deleteSite', 'updateSite', 'onTouch']);

const props = defineProps<{
  title?: string;
  showDeleteIcon?: boolean;
  id?: number;
  site: SiteFormModel;
  errorMessages?: string[] | null;
  disabledAddress?: boolean;
  disabledNumberOfSolShares?: boolean;
  projectCountry?: string | null;
}>();

const plannedSolshareCount = ref(props.site.plannedSolshareCount ?? 1);
const address = ref(props.site.address ?? '');
const latitude = ref<number | null | undefined>(props.site.latitude);
const longitude = ref<number | null | undefined>(props.site.longitude);
const postcode = ref<string | undefined>(props.site.postcode ?? undefined);
const country = ref<string | undefined>(props.site.country ?? undefined);
const lastSelectedAddress = ref(address.value);
const selectedFromSuggestion = ref(false);
const countries = Object.entries(COUNTRY_CODE).map(([name, code]) => ({ name, code }));
const id = props.id ?? Math.random();
const totallyDisabled = computed(() => props.disabledAddress && props.disabledNumberOfSolShares);

const projectStore = useProjectStore();
const { isUserInUK } = storeToRefs(projectStore);
const siteAddressInputId = computed(() => `site-address-input-${id}`);

const isUkAddressContext = computed(() => {
  const uk = COUNTRY_CODE['United Kingdom'];
  return (
    isUserInUK.value ||
    country.value === uk ||
    props.site.country === uk ||
    props.projectCountry === uk ||
    projectStore.newProject.country === uk ||
    projectStore.newProject.sites.some((site) => site.country === uk)
  );
});

const useIdealPostcodes = computed(
  () => isIdealPostcodesFeatureEnabled() && isUkAddressContext.value,
);

onMounted(() => {
  projectStore.fetchUserCountryCode();
});

const showCountryInput = ref(false);
const updateShowCountryInput = () => {
  const countryIsEmpty = !country.value;
  const addressIsEmpty = address.value.trim() === '';
  showCountryInput.value = countryIsEmpty && !addressIsEmpty;
};

const showPostcodeInput = ref(false);
const updateShowPostcodeInput = () => {
  const postcodeIsEmpty = !postcode.value;
  const addressIsEmpty = address.value.trim() === '';
  showPostcodeInput.value = postcodeIsEmpty && !addressIsEmpty;
};

const decrement = () => {
  emit('onTouch');
  if (plannedSolshareCount.value > 1) {
    plannedSolshareCount.value--;
  }
};
const increment = () => {
  emit('onTouch');
  plannedSolshareCount.value++;
};

const placeInputChangedHandler = (newText: string) => {
  emit('onTouch');
  if (selectedFromSuggestion.value) {
    selectedFromSuggestion.value = false;
    return;
  }

  if (newText !== lastSelectedAddress.value) {
    latitude.value = null;
    longitude.value = null;
    postcode.value = undefined;
    country.value = undefined;
  }
};

const placeInputBlurredHandler = () => {
  emit('onTouch');
  updateShowCountryInput();
  updateShowPostcodeInput();
};

const placeChangedHandler = (formatted: Record<string, unknown>) => {
  emit('onTouch');
  const lat = formatted.latitude;
  const lng = formatted.longitude;
  const postalCode = formatted.postal_code;
  const countryName = formatted.country;
  latitude.value = typeof lat === 'number' ? lat : Number(lat);
  longitude.value = typeof lng === 'number' ? lng : Number(lng);
  postcode.value = typeof postalCode === 'string' ? postalCode : undefined;
  country.value =
    typeof countryName === 'string'
      ? (COUNTRY_CODE as Record<string, string>)[countryName]
      : undefined;
  selectedFromSuggestion.value = true;
  lastSelectedAddress.value = address.value;
  updateShowCountryInput();
  updateShowPostcodeInput();
};

const idealPostcodesAddressSelectedHandler = (payload: AddressSelectedPayload) => {
  emit('onTouch');
  address.value = payload.fullAddress ?? payload.line1;
  latitude.value = payload.latitude ?? null;
  longitude.value = payload.longitude ?? null;
  postcode.value = payload.postcode ?? undefined;
  country.value = payload.country ?? undefined;
  selectedFromSuggestion.value = true;
  lastSelectedAddress.value = address.value;
  updateShowCountryInput();
  updateShowPostcodeInput();
};

const onIdealAddressInput = (newText: string) => {
  address.value = newText;
  placeInputChangedHandler(newText);
};

const emitUpdateSite = () => {
  const site = new SiteFormModel().deserialize({
    ...props.site,
    address: address.value,
    plannedSolshareCount: plannedSolshareCount.value,
    latitude: latitude.value,
    longitude: longitude.value,
    postcode: postcode.value,
    country: country.value ?? null,
  });
  emit('updateSite', site);
};

const emitUpdateSiteDebounced = debounce(emitUpdateSite, 500);

watch(address, () => {
  updateShowCountryInput();
  updateShowPostcodeInput();
  emitUpdateSiteDebounced();
});

watch([plannedSolshareCount, latitude, longitude, postcode, country], () => {
  emitUpdateSite();
});

const deleteSite = () => {
  emit('onTouch');
  emit('deleteSite');
};

const onPostcodeChange = () => {
  emit('onTouch');
};

const onPostcodeBlur = () => {
  // trigger on blur so user can enter postcode with space in between, which is common in UK
  postcode.value = postcode.value?.trim();
};

const onCountryChange = () => {
  emit('onTouch');
};
</script>

<template>
  <div class="error-container" v-if="errorMessages?.length">
    <WarningBox type="error">
      <template #title>{{
        errorMessages.length === 1 ? errorMessages[0] : 'Please fix the following errors:'
      }}</template>
      <template #message v-if="errorMessages.length > 1">
        <ul>
          <li v-for="(error, index) in errorMessages" :key="index">{{ error }}</li>
        </ul>
      </template>
    </WarningBox>
  </div>
  <div class="details-container">
    <div class="icon-container icon-container--location">
      <LocationIcon />
    </div>
    <div class="fields-container">
      <div
        v-if="showDeleteIcon && !totallyDisabled"
        class="icon-container--action"
        @click="deleteSite"
      >
        <DeleteIcon data-tn="delete-site-btn" />
      </div>
      <div class="field-container">
        <div v-if="disabledAddress" class="field-container__address-text-container">
          <span class="field-container__label">{{ title }}: </span>
          <span class="field-container__address">{{ site.address }}</span>
        </div>
        <template v-else>
          <a-form-item name="siteAddress">
            <template #label>
              <span>{{ title || 'Enter a site address' }}</span>
            </template>
            <IdealPostcodesAutocomplete
              v-if="useIdealPostcodes"
              :inputFieldId="siteAddressInputId"
              :value="address"
              :projectCountry="COUNTRY_CODE['United Kingdom']"
              :postcode="postcode"
              fullAddress
              classname="input-box"
              placeholder="Type address"
              data-tn="site-address-input"
              size="large"
              autocomplete="off"
              :disabled="disabledAddress"
              @update:value="onIdealAddressInput"
              @address-selected="idealPostcodesAddressSelectedHandler"
              @blur="placeInputBlurredHandler"
            />
            <GoogleAutocomplete
              v-else
              :id="String(id)"
              v-model="address"
              classname="input-box"
              :country="countries.map((entry) => entry.code)"
              :enable-geolocation="true"
              placeholder="Type address"
              @placechanged="placeChangedHandler"
              @blur="placeInputBlurredHandler"
              @change="placeInputChangedHandler"
              :disabled="disabledAddress"
            />
            <p v-if="!disabledAddress" class="field-container__description">
              If you don't see the address, enter it manually.
            </p>
          </a-form-item>

          <a-form-item v-if="showPostcodeInput" name="postcode" label="Postcode">
            <a-input
              v-model:value="postcode"
              size="large"
              class="input-box"
              placeholder="Please input postcode"
              data-tn="site-address-postcode"
              :disabled="disabledAddress"
              autocomplete="off"
              @change="onPostcodeChange"
              @blur="onPostcodeBlur"
            />
          </a-form-item>

          <a-form-item v-if="showCountryInput" name="country" label="Country">
            <a-select
              v-model:value="country"
              size="large"
              class="input-box"
              placeholder="Please input country"
              data-tn="site-address-country"
              :disabled="disabledAddress"
              autocomplete="off"
              @change="onCountryChange"
            >
              <a-select-option
                v-for="entry in countries"
                :key="entry.code"
                :value="entry.code"
                :data-tn="`site-address-country-${entry.code}`"
              >
                {{ entry.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </template>
      </div>
      <div class="number-field-container">
        <p class="number-field-container__label">Number of SolShares at this site</p>
        <div class="number-field">
          <ButtonPrimary
            class="action-btn"
            @click="decrement"
            data-tn="decrement-solshare-count"
            :disabled="disabledNumberOfSolShares"
            >-</ButtonPrimary
          >
          <p class="number-field__display">{{ plannedSolshareCount }}</p>
          <ButtonPrimary
            class="action-btn"
            @click="increment"
            data-tn="increment-solshare-count"
            :disabled="disabledNumberOfSolShares"
            >+</ButtonPrimary
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.details-container {
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  gap: 2px;
  width: 100%;
}

.error-container {
  margin-bottom: 10px;
}

.icon-container {
  flex: 0 0 20px;
  padding-top: 6px;
  color: @color-primary;

  svg {
    width: 20px;
    height: 20px;
  }
}

.icon-container--action {
  position: absolute;
  top: 5px;
  right: 0;
  cursor: pointer;
  z-index: 1;
  color: @color-secondary;
  svg {
    width: 20px;
    height: 20px;
  }
}

.fields-container {
  position: relative;
  width: 100%;
}

.field-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;

  :deep(.ant-form-item) {
    margin-bottom: 10px;
    width: 100%;
  }

  :deep(.ant-form-item-label) {
    padding-bottom: 0;
    width: 100%;
    text-align: start;
  }
  :deep(.ant-form-item-label label) {
    color: @color-primary;
  }

  &__description {
    font-size: 12px;
    color: @color-secondary;
    margin: 8px 0 0 0;
  }
  &__address-text-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: start;
    justify-content: start;
    padding-top: 6px;
    width: 100%;
    margin-bottom: 10px;
  }
  &__label {
    font-weight: @font-weight-medium;
    margin: 0;
    flex: 0 0 100px;
  }
  &__address {
    color: @color-primary;
    margin: 0;
    flex: 1 0 80%;
  }
}

@media (max-width: @mobile-width) {
  .field-container {
    &__address-text-container {
      padding-top: 0;
    }
    &__label,
    &__address {
      flex: 0 0 100%;
    }
  }
}

.number-field-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  gap: 10px;

  &__label {
    font-weight: @font-weight-medium;
    margin: 0;
    flex: 0 1 60%;
  }
}

.number-field {
  flex: 1 0 30%;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 10px;

  &__display {
    font-size: 30px;
    margin: 0 6px;
  }
  .action-btn {
    width: 40px;
    height: 40px !important;
    border-radius: 50% !important;
    font-size: 30px;
    font-weight: lighter;
  }
}
</style>
